/* ==========================================================================
   CUSTOM: Personalise block (PDP) — per-variant personalisation input
   Loaded by sections/main-product.liquid ({%- when 'personalise' -%})

   Shows the field only while the selected variant has the personalise
   metafield set to true. No page reload: variants.js updates
   `#product-form-<section> input[name="id"]` and fires a bubbling `change`
   on it for every variant switch, so that input is the source of truth.

   When hidden the input is disabled, so `properties[...]` is never posted for
   a non-personalisable variant.
   ========================================================================== */
(function () {
  'use strict';

  class PersonaliseField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-pf-input]');
      this.counter = this.querySelector('[data-pf-counter]');
      if (!this.input) return;

      this.formId = this.dataset.formId || '';
      this.required = this.dataset.required === 'true';
      this.maxLength = parseInt(this.dataset.maxLength, 10) || 0;

      // Variant IDs that carry personalise = true
      this.enabledIds = (this.dataset.personaliseVariants || '')
        .split(',')
        .map(function (id) { return id.trim(); })
        .filter(Boolean);

      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onType = this.onType.bind(this);

      this.variantInput = this.findVariantInput();
      if (this.variantInput) {
        this.variantInput.addEventListener('change', this.onVariantInputChange);
      }

      // Backup hook: variants.js also emits this on the picker, and it bubbles.
      // Covers any path that swaps the variant without touching the hidden input.
      this.onVariantChangeEnd = this.onVariantChangeEnd.bind(this);
      document.addEventListener('variantChangeEnd', this.onVariantChangeEnd);

      this.input.addEventListener('input', this.onType);

      this.sync();
    }

    disconnectedCallback() {
      if (this.variantInput) {
        this.variantInput.removeEventListener('change', this.onVariantInputChange);
      }
      document.removeEventListener('variantChangeEnd', this.onVariantChangeEnd);
    }

    findVariantInput() {
      var form = this.formId ? document.getElementById(this.formId) : null;
      if (form) {
        var scoped = form.querySelector('input[name="id"]');
        if (scoped) return scoped;
      }
      return document.querySelector('form[action*="/cart/add"] input[name="id"]');
    }

    currentVariantId() {
      // Re-resolve each time: the theme can replace parts of the product form.
      if (!this.variantInput || !this.variantInput.isConnected) {
        this.variantInput = this.findVariantInput();
      }
      return this.variantInput ? String(this.variantInput.value || '').trim() : '';
    }

    onVariantInputChange() {
      this.sync();
    }

    onVariantChangeEnd() {
      // The hidden input is updated just before this fires; defer a tick so we
      // always read the new value rather than the old one.
      var self = this;
      setTimeout(function () { self.sync(); }, 0);
    }

    sync() {
      var id = this.currentVariantId();
      var show = id !== '' && this.enabledIds.indexOf(id) !== -1;

      this.hidden = !show;
      // Disabled inputs are not submitted — keeps properties[] off non-personalise variants.
      this.input.disabled = !show;

      if (show) {
        if (this.required) this.input.setAttribute('required', 'required');
      } else {
        this.input.removeAttribute('required');
      }

      this.updateCounter();
    }

    onType() {
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counter || !this.maxLength) return;
      var used = (this.input.value || '').length;
      this.counter.textContent = used + '/' + this.maxLength;
      this.counter.classList.toggle('is-limit', used >= this.maxLength);
    }
  }

  if (!customElements.get('personalise-field')) {
    customElements.define('personalise-field', PersonaliseField);
  }
})();
